import { Stack, SxProps, Theme, Typography, useTheme } from "@mui/material";
import { CheckCircleIcon, ClockFilledIcon, CloseIcon } from "components/icons";
import React from "react";

interface TableChipProps {
  content: string;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  containerStyle?: SxProps<Theme>;
  contentStyle?: SxProps<Theme>;
  type: "success" | "error" | "warning" | "neutral";
}
// fills the chip's 16px content box (24px chip, 4px padding) — design
const DEFAULT_ICON_SX = { fontSize: "16px" };
// ClockFilledIcon draws its circle at 62.5% of its box vs the check's 75%;
// scale it 1.2x so both circles match (transform keeps the 16px layout slot)
const CLOCK_ICON_SX = { ...DEFAULT_ICON_SX, transform: "scale(1.2)" };

export const TableChip = ({
  content,
  startIcon,
  endIcon,
  containerStyle,
  type,
  contentStyle,
}: TableChipProps) => {
  const theme = useTheme();
  const chipColors = {
    success: {
      bg: theme.palette.success.tint,
      color: theme.palette.success.main,
    },
    error: {
      bg: theme.palette.error.tint,
      color: theme.palette.error.main,
    },
    warning: {
      bg: theme.palette["warning-minor"].tint,
      color: theme.palette["warning-minor"].main,
    },
    neutral: {
      bg: theme.palette.neutral["010"],
      color: theme.palette.lightBg.low,
    },
  };

  const getDefualtStartIcon = () => {
    switch (type) {
      case "success":
        return (
          <CheckCircleIcon
            sx={DEFAULT_ICON_SX}
            htmlColor={chipColors[type]?.color}
          />
        );
      case "error":
        return (
          <CloseIcon sx={DEFAULT_ICON_SX} htmlColor={chipColors[type]?.color} />
        );
      case "neutral":
        return (
          <ClockFilledIcon
            sx={CLOCK_ICON_SX}
            htmlColor={chipColors[type]?.color}
          />
        );
      default:
        break;
    }
  };
  return (
    <Stack
      direction={"row"}
      gap="4px"
      sx={{
        alignItems: "center",
        padding: "4px 8px",
        width: "102px",
        borderRadius: "4px",
        height: "24px",
        // 24px includes the padding. A select's value area is content-box and
        // children inherit it, so without this the chip grows to ~34px there.
        boxSizing: "border-box",
        backgroundColor: chipColors[type]?.bg,
        color: chipColors[type]?.color,
        ...containerStyle,
      }}
    >
      {startIcon ? startIcon : getDefualtStartIcon()}
      <Typography
        variant="caption-3"
        color={theme.palette.lightBg.high}
        sx={{ ...contentStyle }}
      >
        {content}
      </Typography>
      <Stack
        alignItems={"center"}
        direction={"row"}
        sx={{ alignSelf: "flex-end" }}
      >
        {endIcon}
      </Stack>
    </Stack>
  );
};
